import { useState, useEffect, useRef } from "react";
import { getSession } from "../service/auth";
import {
  getEmployeeByUserId,
  getOpenAttendance,
  getTodayWorkedHours,
  checkIn,
  checkOut,
} from "../service/rpc";

/* ── helpers ── */
const pad = (n) => String(n).padStart(2, "0");

const formatDuration = (totalSeconds) => {
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = Math.floor(totalSeconds % 60);
  return `${pad(h)}:${pad(m)}:${pad(s)}`;
};

const hoursToSeconds = (h) => Math.round((h || 0) * 3600);

export default function CheckInWidget({ onNavigateToAttendance }) {
  const session = getSession();

  const employeeRef = useRef(null);                     // stable ref — no stale closure
  const [employee, setEmployee] = useState(null);
  const [openRecord, setOpenRecord] = useState(null);
  const [todayPrevSecs, setTodayPrevSecs] = useState(0);
  const [liveSecs, setLiveSecs] = useState(0);
  const [popupOpen, setPopupOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [initialised, setInitialised] = useState(false);

  const intervalRef = useRef(null);
  const popupRef = useRef(null);

  /* ── load employee + attendance state ── */
  const loadState = async () => {
    if (!session?.uid) return;
    try {
      let emp = employeeRef.current;
      if (!emp) {
        emp = await getEmployeeByUserId(session.uid);
        if (!emp) { setInitialised(true); return; }
        employeeRef.current = emp;
        setEmployee(emp);
      }

      const [open, todayRecs] = await Promise.all([
        getOpenAttendance(emp.id),
        getTodayWorkedHours(emp.id),
      ]);

      // sum closed records for today
      const closedSecs = todayRecs
        .filter((r) => r.check_out)
        .reduce((acc, r) => acc + hoursToSeconds(r.worked_hours), 0);
      setTodayPrevSecs(closedSecs);

      setOpenRecord(open || null);
      if (open) {
        const dt = open.check_in.replace(" ", "T") + "Z";
        const elapsedSecs = Math.floor((Date.now() - new Date(dt).getTime()) / 1000);
        setLiveSecs(Math.max(0, elapsedSecs));
      } else {
        setLiveSecs(0);
      }
    } catch (e) {
      console.error("CheckInWidget loadState", e);
    } finally {
      setInitialised(true);
    }
  };

  useEffect(() => { loadState(); }, []);

  /* ── live clock when checked in ── */
  useEffect(() => {
    clearInterval(intervalRef.current);
    if (openRecord) {
      intervalRef.current = setInterval(() => {
        setLiveSecs((s) => s + 1);
      }, 1000);
    }
    return () => clearInterval(intervalRef.current);
  }, [openRecord]);

  /* ── close popup on outside click ── */
  useEffect(() => {
    const handler = (e) => {
      if (popupRef.current && !popupRef.current.contains(e.target)) {
        setPopupOpen(false);
      }
    };
    if (popupOpen) document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [popupOpen]);

  const isCheckedIn = !!openRecord;
  const totalTodaySecs = todayPrevSecs + liveSecs;

  /* ── check in handler ── */
  const handleCheckIn = async () => {
    const emp = employeeRef.current;
    if (!emp || loading) return;
    setLoading(true);
    try {
      const newId = await checkIn(emp.id);
      const now = new Date().toISOString().replace("T", " ").slice(0, 19);
      setOpenRecord({ id: newId, check_in: now, employee_id: [emp.id, emp.name] });
      setLiveSecs(0);
    } catch (e) {
      alert("Check-in failed: " + e.message);
    } finally {
      setLoading(false);
    }
  };

  /* ── check out handler ── */
  const handleCheckOut = async () => {
    if (!openRecord || loading) return;
    setLoading(true);
    try {
      await checkOut(openRecord.id);
      setTodayPrevSecs((p) => p + liveSecs);
      setOpenRecord(null);
      setLiveSecs(0);
    } catch (e) {
      alert("Check-out failed: " + e.message);
    } finally {
      setLoading(false);
    }
  };

  if (!initialised || !employee) return null;

  const checkInTime = openRecord
    ? new Date(openRecord.check_in.replace(" ", "T") + "Z")
      .toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
    : null;

  return (
    <div className="ci-widget" ref={popupRef}>
      {/* ── Trigger Button ── */}


      {/* ── Popup ── */}
      {popupOpen && (
        <div className="ci-popup">
          {isCheckedIn ? (
            <>
              <div className="ci-popup__row ci-popup__row--split">
                <div className="ci-popup__stat">
                  <span className="ci-popup__label">Since {checkInTime}</span>
                  <span className="ci-popup__timer ci-popup__timer--live">{formatDuration(liveSecs)}</span>
                </div>
                <div className="ci-popup__stat">
                  <span className="ci-popup__label">Before {checkInTime}</span>
                  <span className="ci-popup__timer">{formatDuration(todayPrevSecs)}</span>
                </div>
              </div>
              <div className="ci-popup__divider" />
              <div className="ci-popup__total-row">
                <span className="ci-popup__total-label">Total today</span>
                <span className="ci-popup__total-val">{formatDuration(totalTodaySecs)}</span>
              </div>
              <button
                className="ci-popup__action ci-popup__action--out"
                onClick={() => { handleCheckOut(); setPopupOpen(false); }}
                disabled={loading}
              >
                {loading ? <span className="ci-spinner ci-spinner--sm" /> : <>Check out <span className="ci-arrow">→</span></>}
              </button>
            </>
          ) : (
            <>
              <div className="ci-popup__total-row">
                <span className="ci-popup__total-label">Total today</span>
                <span className="ci-popup__total-val">{formatDuration(totalTodaySecs)}</span>
              </div>
              <button
                className="ci-popup__action ci-popup__action--in"
                onClick={() => { handleCheckIn(); setPopupOpen(false); }}
                disabled={loading}
              >
                {loading ? <span className="ci-spinner ci-spinner--sm" /> : <>Check in <span className="ci-arrow">→</span></>}
              </button>
            </>
          )}

          {onNavigateToAttendance && (
            <>
              <div className="ci-popup__divider" />
              <button
                className="ci-popup__link"
                onClick={() => { setPopupOpen(false); onNavigateToAttendance(); }}
              >
                View all attendance records →
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
